/**
 * Tradução do protocolo do servidor para as entidades do domínio.
 *
 * O servidor manda tupla (array posicional), não objeto — os índices abaixo
 * são os mesmos constantes de `CValidaSync.java` do TMobi. Mexer aqui sem
 * mexer no servidor quebra a validação silenciosamente, então cada índice está
 * nomeado.
 */

import type {
  Evento,
  IngressoCancelado,
  IngressoOnline,
  Lote,
  Setor,
} from '../dominio/tipos';

type Tupla = Array<string | number>;

// `online`: [codbar, email, nome, cpf, pedido, setor, numero, setorId, status]
const ON_CODBAR = 0;
const ON_EMAIL = 1;
const ON_NOME = 2;
const ON_CPF = 3;
const ON_PEDIDO = 4;
const ON_SETOR = 5;
const ON_NUMERO = 6;
const ON_SETOR_ID = 7;
const ON_STATUS = 8;

// `cancelados`: [codbar, dataCancelamento, usuario, motivo]
const CA_CODBAR = 0;
const CA_DATA = 1;
const CA_USUARIO = 2;
const CA_MOTIVO = 3;

// `codes`: [consumacao, numeroDoLote, setor, setorId]
const CO_CONSUMACAO = 0;
const CO_LOTE = 1;
const CO_SETOR = 2;
const CO_SETOR_ID = 3;

// `setores`: [remoteId, nome]
const SE_REMOTE_ID = 0;
const SE_NOME = 1;

const texto = (t: Tupla, i: number): string => String(t[i] ?? '');
const numero = (t: Tupla, i: number): number => Number(t[i] ?? 0);

/**
 * Monta a `loteStr` exatamente como o app antigo: o número do lote seguido da
 * forma preenchida com "4" até 4 dígitos — é essa segunda parte que aparece
 * dentro do código de barras e que a regra casa com LIKE.
 *
 * Ex.: lote "123" vira "123 - 4123".
 */
export function montarLoteStr(numeroDoLote: string): string {
  let preenchido = String(parseInt(numeroDoLote, 10));
  while (preenchido.length < 4) {
    preenchido = '4' + preenchido;
  }
  return numeroDoLote + ' - ' + preenchido;
}

export function lerLotes(codes: Tupla[], eventoId: number): Lote[] {
  return codes.map(t => ({
    eventoId,
    loteStr: montarLoteStr(texto(t, CO_LOTE)),
    setor: texto(t, CO_SETOR),
    setorId: numero(t, CO_SETOR_ID),
    consumacao: String(t[CO_CONSUMACAO] ?? '0'),
  }));
}

export function lerSetores(
  setores: Tupla[],
  eventoId: number,
): Array<Omit<Setor, 'habilitado' | 'id'>> {
  return setores.map(t => ({
    remoteId: numero(t, SE_REMOTE_ID),
    eventoId,
    nome: texto(t, SE_NOME),
  }));
}

export function lerCancelados(
  cancelados: Tupla[],
  eventoId: number,
): IngressoCancelado[] {
  return cancelados.map(t => ({
    eventoId,
    codbar: texto(t, CA_CODBAR),
    dataCancelamento: texto(t, CA_DATA),
    usuario: texto(t, CA_USUARIO),
    motivoCancelamento: texto(t, CA_MOTIVO),
  }));
}

export function lerIngressosOnline(
  online: Tupla[],
  eventoId: number,
): IngressoOnline[] {
  return online.map(t => ({
    eventoId,
    codbar: texto(t, ON_CODBAR),
    email: texto(t, ON_EMAIL),
    nome: texto(t, ON_NOME),
    cpf: texto(t, ON_CPF),
    pedidoId: texto(t, ON_PEDIDO),
    setor: texto(t, ON_SETOR),
    numero: texto(t, ON_NUMERO),
    setorId: numero(t, ON_SETOR_ID),
    status: texto(t, ON_STATUS),
  }));
}

/** `validados` vem só com o código: são validações feitas em outros aparelhos. */
export function lerValidados(validados: Tupla[]): string[] {
  return validados.map(t => texto(t, 0)).filter(c => c.length > 0);
}

/**
 * Eventos que este operador pode validar.
 *
 * O `getAllData` devolve TODOS os eventos da ticketeira e diz num campo
 * `status` quais valem para quem pediu: "A" quando o operador está vinculado,
 * outra letra quando não — desvincular no painel faz o evento descer como "R".
 * É o mesmo filtro que a lista do TMobi original faz (`where status = 'A'`, em
 * `EventosActivity`), e sem ele o evento continua na portaria depois de tirarem
 * o operador dele.
 *
 * Ausência do campo conta como ativo, de propósito: se o servidor de alguma
 * ticketeira não mandar `status`, o certo é a portaria ver os eventos e não
 * ficar com a lista vazia.
 */
export function lerEventos(eventos: Array<Record<string, unknown>>): Evento[] {
  return eventos
    .filter(e => e.status == null || String(e.status) === 'A')
    .map(e => ({
      id: Number(e.remoteId),
      nome: String(e.nome ?? ''),
      data: e.data ? String(e.data) : undefined,
      local: e.local ? String(e.local) : undefined,
      cidade: e.cidade ? String(e.cidade) : undefined,
      // `bannerMd5` guarda a URL inteira da imagem, não um md5 — o nome ficou
      // de quando era o nome do arquivo. O TMobi original entrega o campo
      // direto ao carregador de imagens, sem montar base nenhuma
      // (`EventosActivity.getView`).
      banner: e.bannerMd5 ? String(e.bannerMd5) : undefined,
    }));
}

/** O servidor espera a data neste formato no `datahora` e nos validados. */
export function formatarDataHora(data: Date): string {
  const dois = (n: number) => String(n).padStart(2, '0');
  return (
    data.getFullYear() +
    '-' +
    dois(data.getMonth() + 1) +
    '-' +
    dois(data.getDate()) +
    ' ' +
    dois(data.getHours()) +
    ':' +
    dois(data.getMinutes()) +
    ':' +
    dois(data.getSeconds())
  );
}
